import { baseApi as api } from "../../../contexts/endpoints/base_api";

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCommunication: build.query<
      GetCommunicationApiResponse,
      GetCommunicationApiArg
    >({
      query: () => ({ url: `/api/account/communication` }),
      providesTags: ["EmailPreferences"],
    }),
    putAccountSub: build.mutation<
      PutAccountSubApiResponse,
      PutAccountSubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/account`,
        method: "PUT",
        body: queryArg.accountSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putNewsFeedSub: build.mutation<
      PutNewsFeedSubApiResponse,
      PutNewsFeedSubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/newsfeed`,
        method: "PUT",
        body: queryArg.newsfeedSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putWebinarSub: build.mutation<
      PutWebinarSubApiResponse,
      PutWebinarSubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/webinars`,
        method: "PUT",
        body: queryArg.webinarsSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putProductSupportSub: build.mutation<
      PutProductSupportSubApiResponse,
      PutProductSupportSubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/ignitionSupportAndProductUpdates`,
        method: "PUT",
        body: queryArg.ignitionSupportAndProductUpdatesSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putIntegratorSub: build.mutation<
      PutIntegratorSubApiResponse,
      PutIntegratorSubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/integrators`,
        method: "PUT",
        body: queryArg.integratorsSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putUserFeedbackGroupSub: build.mutation<
      PutUserFeedbackGroupSubApiResponse,
      PutUserFeedbackGroupSubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/userFeedbackGroup`,
        method: "PUT",
        body: queryArg.userFeedbackGroupSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putIccSub: build.mutation<PutIccSubApiResponse, PutIccSubApiArg>({
      query: (queryArg) => ({
        url: `/api/account/communication/ignitionCommunityConference`,
        method: "PUT",
        body: queryArg.ignitionCommunityConferenceSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putWorkspaceSub: build.mutation<
      PutWorkspaceSubApiResponse,
      PutWorkspaceSubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/workspace`,
        method: "PUT",
        body: queryArg.workspaceSubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putWorkspacesActivitySub: build.mutation<
      PutWorkspacesActivitySubApiResponse,
      PutWorkspacesActivitySubApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/communication/workspacesActivity`,
        method: "PUT",
        body: queryArg.workspacesActivitySubPayload,
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
    putUnsubscribeAll: build.mutation<
      PutUnsubscribeAllApiResponse,
      PutUnsubscribeAllApiArg
    >({
      query: () => ({
        url: `/api/account/communication/unsubscribeAll`,
        method: "PUT",
      }),
      invalidatesTags: ["EmailPreferences"],
    }),
  }),
  overrideExisting: false,
});

export type GetCommunicationApiResponse =
  /** status 200 OK */ EmailPreferencesResponse & {
    // Populated by the BE in TS-2761. Until that ships the field is absent, so
    // it stays optional and the Hubspot Manage button hides when it is missing.
    vendorEmailPreferencesManagementUrl?: string;
  };
export type GetCommunicationApiArg = void;
export type PutAccountSubApiResponse = /** status 200 OK */ AccountSubPayload;
export type PutAccountSubApiArg = {
  accountSubPayload: AccountSubPayload;
};
export type PutNewsFeedSubApiResponse = /** status 200 OK */ NewsfeedSubPayload;
export type PutNewsFeedSubApiArg = {
  newsfeedSubPayload: NewsfeedSubPayload;
};
export type PutWebinarSubApiResponse = /** status 200 OK */ WebinarsSubPayload;
export type PutWebinarSubApiArg = {
  webinarsSubPayload: WebinarsSubPayload;
};
export type PutProductSupportSubApiResponse =
  /** status 200 OK */ IgnitionSupportAndProductUpdatesSubPayload;
export type PutProductSupportSubApiArg = {
  ignitionSupportAndProductUpdatesSubPayload: IgnitionSupportAndProductUpdatesSubPayload;
};
export type PutIntegratorSubApiResponse =
  /** status 200 OK */ IntegratorsSubPayload;
export type PutIntegratorSubApiArg = {
  integratorsSubPayload: IntegratorsSubPayload;
};
export type PutUserFeedbackGroupSubApiResponse =
  /** status 200 OK */ UserFeedbackGroupSubPayload;
export type PutUserFeedbackGroupSubApiArg = {
  userFeedbackGroupSubPayload: UserFeedbackGroupSubPayload;
};
export type PutIccSubApiResponse =
  /** status 200 OK */ IgnitionCommunityConferenceSubPayload;
export type PutIccSubApiArg = {
  ignitionCommunityConferenceSubPayload: IgnitionCommunityConferenceSubPayload;
};
export type PutWorkspaceSubApiResponse =
  /** status 200 OK */ WorkspaceSubPayload;
export type PutWorkspaceSubApiArg = {
  workspaceSubPayload: WorkspaceSubPayload;
};
export type PutWorkspacesActivitySubApiResponse =
  /** status 200 OK */ WorkspacesActivitySubPayload;
export type PutWorkspacesActivitySubApiArg = {
  workspacesActivitySubPayload: WorkspacesActivitySubPayload;
};
export type PutUnsubscribeAllApiResponse = /** status 200 OK */ object;
export type PutUnsubscribeAllApiArg = void;

export type EmailPreferencesResponse = {
  newsfeed: boolean;
  webinars: boolean;
  ignitionSupportAndProductUpdates: boolean;
  integrators: boolean;
  userFeedbackGroup: boolean;
  ignitionCommunityConference: boolean;
  account: boolean;
  workspace: boolean;
  workspacesActivity?: boolean;
};
export type AccountSubPayload = {
  account: boolean;
};
export type NewsfeedSubPayload = {
  newsfeed: boolean;
};
export type WebinarsSubPayload = {
  webinars: boolean;
};
export type IgnitionSupportAndProductUpdatesSubPayload = {
  ignitionSupportAndProductUpdates: boolean;
};
export type IntegratorsSubPayload = {
  integrators: boolean;
};
export type UserFeedbackGroupSubPayload = {
  userFeedbackGroup: boolean;
};
export type IgnitionCommunityConferenceSubPayload = {
  ignitionCommunityConference: boolean;
};
export type WorkspaceSubPayload = {
  workspace: boolean;
};
export type WorkspacesActivitySubPayload = {
  workspacesActivity: boolean;
};

export const {
  useGetCommunicationQuery,
  usePutAccountSubMutation,
  usePutNewsFeedSubMutation,
  usePutWebinarSubMutation,
  usePutProductSupportSubMutation,
  usePutIntegratorSubMutation,
  usePutUserFeedbackGroupSubMutation,
  usePutIccSubMutation,
  usePutWorkspaceSubMutation,
  usePutWorkspacesActivitySubMutation,
  usePutUnsubscribeAllMutation,
} = injectedApi;
